import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  Container,
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Alert,
  AlertTitle,
  useTheme, // <--- Import useTheme hook to access palette
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import HomeIcon from "@mui/icons-material/Home";
import {
  createLeadFromToken,
  verifyLeadTicket,
} from "../../services/confirmTicket";

const ConfirmTicket = () => {
  const theme = useTheme();
  const location = useLocation();

  // Use state to manage the status internally (e.g., loading while the verification API runs)
  const [status, setStatus] = useState("loading");

  // Use MUI colors for the dark card theme
  const darkCardBg = theme.palette.grey[900]; // A very dark gray/black from the palette
  const textColor = theme.palette.common.white;
  const secondaryTextColor = theme.palette.grey[300]; // Lighter gray for secondary text

  useEffect(() => {
    async function verifyLeadTicketAndCreate() {
      const params = new URLSearchParams(location.search);

      const extractedData = {
        token: params.get("token"),
        fullName: params.get("fullName"),
        phoneNumber: params.get("phoneNumber"),
        subject: params.get("subject"),
        email: params.get("email"),
      };

      setStatus("loading");
      const verifyResult = await verifyLeadTicket(extractedData.token);

      if (!verifyResult.valid) {
        setStatus("failure");
        return;
      }

      const createLeadResult = await createLeadFromToken(
        extractedData.token,
        extractedData.fullName,
        extractedData.subject,
        extractedData.phoneNumber,
        extractedData.email
      );

      if (!createLeadResult.success) {
        setStatus("failure");
        return;
      }
      setStatus("success");
    }

    verifyLeadTicketAndCreate();
  }, []);

  // --- SUCCESS VIEW ---
  const SuccessView = (
    <Paper
      elevation={10}
      sx={{
        p: 4,
        textAlign: "center",
        backgroundColor: darkCardBg, // <--- Use MUI dark color
        borderRadius: 2,
      }}
    >
      <CheckCircleIcon sx={{ fontSize: 80, color: "success.main", mb: 2 }} />
      {/* Set text color to white for contrast */}
      <Typography variant="h4" component="h1" gutterBottom color={textColor}>
        Ticket Confirmed Successfully!
      </Typography>
      <Typography variant="body1" sx={{ mb: 3 }} color={textColor}>
        Your ticket has been successfully verified and submitted.
      </Typography>
      <Box sx={{ mt: 3 }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<HomeIcon />}
          onClick={() => (window.location.href = "/")}
        >
          Go to Homepage
        </Button>
      </Box>
    </Paper>
  );

  // --- FAILURE VIEW ---
  const FailureView = (
    <Paper
      elevation={10}
      sx={{
        p: 4,
        textAlign: "center",
        backgroundColor: darkCardBg, // <--- Use MUI dark color
        borderRadius: 2,
      }}
    >
      <ErrorIcon sx={{ fontSize: 80, color: "error.main", mb: 2 }} />
      {/* Set text color to white for contrast */}
      <Typography variant="h4" component="h1" gutterBottom color={textColor}>
        Verification Failed
      </Typography>
      {/* Alerts use their own color system, so we keep this one as is */}
      <Alert severity="error" sx={{ textAlign: "left", mb: 3 }}>
        <AlertTitle>Could not confirm your ticket.</AlertTitle>
        The link may be **expired (tokens expire after 1 hour)** or invalid.
        Please ensure you are using the most recent confirmation email.
      </Alert>
      <Box sx={{ mt: 3 }}>
        <Button
          variant="outlined"
          color="secondary"
          onClick={() => (window.location.href = "/contact-support")}
        >
          Contact Support Again
        </Button>
      </Box>
    </Paper>
  );

  // --- LOADING VIEW ---
  const LoadingView = (
    <Paper
      elevation={10}
      sx={{
        p: 4,
        textAlign: "center",
        backgroundColor: darkCardBg, // <--- Use MUI dark color
        borderRadius: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {/* Set loader color to white */}
        <CircularProgress size={60} sx={{ mb: 2, color: textColor }} />
        <Typography variant="h6" color={textColor}>
          Verifying your ticket...
        </Typography>
        {/* Use a lighter gray for the secondary text */}
        <Typography variant="body2" color={secondaryTextColor}>
          Please wait, this may take a moment.
        </Typography>
      </Box>
    </Paper>
  );

  const backgroundImageURL =
    "https://airsmart.com/airsmart-cms/wp-content/uploads/2018/04/INFORM_LucasSt11575_R.jpg";

  return (
    <Box
      sx={{
        backgroundImage: `url(${backgroundImageURL})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        py: 4,
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.4)",
          zIndex: 1,
        },
      }}
    >
      <Container maxWidth="sm" sx={{ zIndex: 2, position: "relative" }}>
        {status === "loading" && LoadingView}
        {status === "success" && SuccessView}
        {status === "failure" && FailureView}
      </Container>
    </Box>
  );
};

export default ConfirmTicket;
